import { HStack, Skeleton, Stack, Text, VStack } from '@genial/components';
import { BoxRadius, Color, Typography } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import { TableContent, TableContentItemColumn } from '../../../../../../../components/TableContent';
import {
  DoubleTextSkeleton,
  InputSkeleton,
  RiskSkeleton,
  TextSkeleton,
  TextWithSubtextSkeleton,
} from '../../../../DynamicAsset/components/AssetsLoading';

const Mobile = () => (
  <TableContent direction="column" bg="backgroundlight" space="inline.xxxs">
    <TableContentItemColumn w="full" space="inline.quarck">
      <TextSkeleton w="full" h={Typography['fontsize.xl']} />
      <RiskSkeleton />
    </TableContentItemColumn>
    <VStack w="full" space="inline.nano">
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <RiskSkeleton />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
      <HStack alignItems="center" justifyContent="space-between">
        <TextSkeleton w="40%" />
        <TextSkeleton w="40%" />
      </HStack>
    </VStack>
    <VStack w="full" space="inline.quarck">
      <TextSkeleton />
      <HStack alignItems="center" space="inline.xxxs">
        <Box flex={1}>
          <InputSkeleton />
        </Box>
        <Skeleton
          width="40px"
          height="40px"
          borderRadius={BoxRadius.sm}
          endColor={Color['brand.primary.lightest']}
        />
      </HStack>
    </VStack>
  </TableContent>
);

const Tablet = () => (
  <TableContent direction="column" bg="backgroundlight" space="inline.xxxs">
    <HStack justifyContent="space-between" w="full">
      <TableContentItemColumn space="inline.quarck">
        <TextSkeleton w="1/2" hasCaptions />
      </TableContentItemColumn>
      <Skeleton
        width="40px"
        height="40px"
        borderRadius={BoxRadius.sm}
        endColor={Color['brand.primary.lightest']}
      />
    </HStack>
    <HStack w="full" alignItems="center" justifyContent="space-between">
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <TextSkeleton w="50%" />
        <RiskSkeleton />
      </TableContentItemColumn>
    </HStack>
    <HStack w="full" alignItems="center" justifyContent="space-between">
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <DoubleTextSkeleton w="4/5" />
      </TableContentItemColumn>
      <TableContentItemColumn space="inline.quarck" w="1/4">
        <TextSkeleton />
        <InputSkeleton />
      </TableContentItemColumn>
    </HStack>
  </TableContent>
);

const Desktop = () => (
  <TableContent direction="column" space="stack.xxxs" bg="backgroundlight">
    <HStack w="full" justifyContent="space-between">
      <TableContentItemColumn space="inline.quarck">
        <TextSkeleton w="1/2" hasCaptions />
      </TableContentItemColumn>
      <Skeleton
        width="40px"
        height="40px"
        borderRadius={BoxRadius.sm}
        endColor={Color['brand.primary.lightest']}
      />
    </HStack>
    <HStack w="full" alignItems="center" justifyContent="space-between">
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Aplicação</Text>
          <TextWithSubtextSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Risco</Text>
          <RiskSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">IR</Text>
          <TextSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Rentabilidade</Text>
          <TextWithSubtextSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Vencimento</Text>
          <TextWithSubtextSkeleton />
        </VStack>
      </Stack>
    </HStack>
    <HStack w="full" alignItems="center" justifyContent="space-between">
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Taxa</Text>
          <Stack pr="inline.xxxs">
            <InputSkeleton />
          </Stack>
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Preço unitário</Text>
          <TextSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Fee</Text>
          <TextWithSubtextSkeleton />
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Valor a aplicar</Text>
          <Stack pr="inline.xxxs">
            <InputSkeleton />
          </Stack>
        </VStack>
      </Stack>
      <Stack w="1/5">
        <VStack space="stack.quarck" justifyContent="space-between">
          <Text variant="semibold">Líquido estimado</Text>
          <TextWithSubtextSkeleton />
        </VStack>
      </Stack>
    </HStack>
  </TableContent>
);

export const AddedProductItemFixedIncomeSkeleton: React.FC = () => {
  const layout = useBreakpointValue({ base: <Mobile />, sm: <Tablet />, lg: <Desktop /> });

  return layout;
};
